import { z } from 'zod';
import type { ReplicateClient } from '../replicate';
import { pollPrediction } from '../replicate';
import { getImageModel } from '@/shared/catalog/models';
import { aspectRatioToDimensions, type AspectRatio } from '@/entities/aspect-ratio';

export interface ImageRunInput {
  modelId: string;
  prompt: string;
  aspectRatio: AspectRatio;
  longestSidePx: number;
  numImages: number;
  /** Optional integer seed for diffusion noise control. Both shipped models accept this. */
  seed?: number;
  imageUrls?: readonly string[];
  signal?: AbortSignal;
}

export interface ImageRunOutput {
  imageUrls: string[];
  predictTimeSec?: number;
  replicateId: string;
}

const OutputSchema = z.union([
  z.array(z.string().url()),
  z.string().url(),
  z.array(z.object({ url: z.string().url() })),
]);

/**
 * Generates images via Replicate. Falls back to t2i if the model doesn't
 * support image-to-image edits or no input images are supplied.
 */
export async function runImage(
  client: ReplicateClient,
  input: ImageRunInput,
): Promise<ImageRunOutput> {
  const model = getImageModel(input.modelId);
  if (!model) throw new Error(`Unknown image model: ${input.modelId}`);

  const dims = aspectRatioToDimensions(input.aspectRatio, input.longestSidePx);
  const useEdit =
    model.capabilities.includes('edit') && input.imageUrls && input.imageUrls.length > 0;

  const payload: Record<string, unknown> = {
    prompt: input.prompt,
    width: dims.width,
    height: dims.height,
    num_outputs: input.numImages,
    aspect_ratio: input.aspectRatio,
  };
  if (input.seed !== undefined) payload.seed = input.seed;
  // Replicate's edit-capable models we ship (google/nano-banana-2,
  // bytedance/seedream-4) both use `image_input` for reference images, not
  // `image_urls`. Sending the wrong key is silently ignored and yields a pure
  // t2i render unrelated to the selected product image.
  if (useEdit && input.imageUrls) payload.image_input = [...input.imageUrls];

  const prediction = await client.createPrediction({
    model: input.modelId,
    input: payload,
    ...(input.signal && { signal: input.signal }),
  });

  const final = await pollPrediction(client, prediction.id, {
    ...(input.signal && { signal: input.signal }),
  });

  if (final.status !== 'succeeded') {
    throw new Error(
      `Image run failed: ${typeof final.error === 'string' ? final.error : 'unknown error'}`,
    );
  }

  const parsed = OutputSchema.safeParse(final.output);
  let urls: string[] = [];
  if (parsed.success) {
    if (typeof parsed.data === 'string') urls = [parsed.data];
    else if (Array.isArray(parsed.data) && parsed.data.length > 0) {
      urls = (parsed.data as Array<string | { url: string }>).map((v) =>
        typeof v === 'string' ? v : v.url,
      );
    }
  }

  return {
    imageUrls: urls,
    replicateId: final.id,
    ...(final.metrics?.predict_time !== undefined && {
      predictTimeSec: final.metrics.predict_time,
    }),
  };
}
